import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { DirectoryEntityType,
    DirectoryPharmacySearchDto
} from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { ContactDirectoryClient } from "../Api/ContactDirectoryClient";
import { DirectoryPharmacyMapper } from "../Api/Mapper/DirectoryPharmacyMapper";
import { ContactDirectoryHelper } from "../Helpers/ContactDirectoryHelper";
import { PreferredPharmacyHelper } from "../Helpers/PreferredPharmacyHelper";
import { DirectoryPharmacy } from "../Models/DirectoryPharmacy";
import { DirectoryFaxNumber } from "./DirectoryFaxNumber";
import { DirectoryPhoneNumber } from "./DirectoryPhoneNumber";
import { DirectorySelectableEntity } from "./DirectorySelectableEntity";
import { IGetDisplayName } from "./IGetDisplayName";

export class PharmacyProxy implements IGetDisplayName {
    // data from Myle
    public CustomPharmacy: Pharmacy;
    public DirectoryPharmacyId: string;

    // data from Contact Directory
    private directoryPharmacy: DirectoryPharmacy;

    private isLoadingDirectoryPharmacy: boolean = false;
    private client = ContactDirectoryClient.getInstance();

    public constructor(options: {
        customPharmacy?: Pharmacy,
        directoryPharmacyId?: string,
        directoryPharmacy?: DirectoryPharmacy,
    }) {
        this.CustomPharmacy = options.customPharmacy;
        this.DirectoryPharmacyId = options.directoryPharmacy?.id ?? options.directoryPharmacyId;
        this.directoryPharmacy = options.directoryPharmacy;
    }

    public getId(): string {
        return this.DirectoryPharmacyId ?? "custom_pharmacy_id";
    }

    public async loadDirectoryPharmacy(): Promise<void> {
        if (!this.DirectoryPharmacyId)
            return null;

        if (!this.directoryPharmacy && !this.isLoadingDirectoryPharmacy && cacheStore.ClinicSettings.EnableContactDirectory) {
            this.isLoadingDirectoryPharmacy = true;

            try {
                const pharmacyDto = (await this.client.getByIds(DirectoryEntityType.Pharmacy, [this.DirectoryPharmacyId]))
                    .firstOrDefault() as DirectoryPharmacySearchDto;
                this.directoryPharmacy = pharmacyDto ? DirectoryPharmacy.fromSearchDto(pharmacyDto) : null;
            }
            finally {
                this.isLoadingDirectoryPharmacy = false;
            }
        }
    }

    public getDirectoryPharmacy(): DirectoryPharmacy {
        if (!this.DirectoryPharmacyId)
            return null;

        return this.directoryPharmacy;
    }

    public hasValue(): boolean {
        return (cacheStore.ClinicSettings.EnableContactDirectory && this.DirectoryPharmacyId && !!this.directoryPharmacy)
            || !!this.CustomPharmacy;
    }

    public getPharmacyInfo(shouldFormatPhoneAndFax: boolean = true, preferredPharmacyHelper?: PreferredPharmacyHelper): IPharmacyInfo {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.DirectoryPharmacyId) {
            const directoryPharmacy = this.getDirectoryPharmacy();
            if (directoryPharmacy)
                return directoryPharmacy.getPharmacyInfo(shouldFormatPhoneAndFax);
        }

        if (!this.CustomPharmacy)
            return {} as IPharmacyInfo;

        const { country, state } = preferredPharmacyHelper?.getCountryAndState() ?? {};

        return shouldFormatPhoneAndFax
            ? {
                ...this.CustomPharmacy,
                Phone: PhoneNumberHelper.format(this.CustomPharmacy.Phone, country?.Id, state?.Id),
                Fax: PhoneNumberHelper.format(this.CustomPharmacy.Fax, country?.Id, state?.Id)
            }
            : { ...this.CustomPharmacy };
    }

    public getDisplayName(): string {
        const directoryPharmacy = this.getDirectoryPharmacy();
        const pharmacyInfo = this.getPharmacyInfo();

        if (directoryPharmacy)
            return directoryPharmacy?.getDisplayName();

        var displayName = "";
        if (pharmacyInfo.Banner)
            displayName += pharmacyInfo.Banner;

        if (pharmacyInfo.Name)
            displayName += `${pharmacyInfo.Banner ? ` - ${pharmacyInfo.Name}` : pharmacyInfo.Name}`;

        return displayName;
    }

    public toDirectorySelectableEntity(): DirectorySelectableEntity<DirectoryPharmacy> {
        if (this.directoryPharmacy)
            return DirectoryPharmacyMapper.toDirectorySelectableEntity(this.directoryPharmacy);

        if (!this.CustomPharmacy)
            return null;

        const { defaultCountry } = ContactDirectoryHelper.getDefaultCountryAndState();
        const pharmacyInfo = this.getPharmacyInfo();

        return new DirectorySelectableEntity({
            id: null,
            type: DirectoryEntityType.Pharmacy,
            name: this.getDisplayName(),
            address: pharmacyInfo.Address,
            phoneNumbers: pharmacyInfo.Phone
                ? [new DirectoryPhoneNumber({ id: "custom_pharmacy_phoneNumber", number: pharmacyInfo.Phone, countryId: defaultCountry.Id })]
                : [],
            faxInfos: pharmacyInfo.Fax
                ? [{ faxNumber: new DirectoryFaxNumber({ id: "custom_pharmacy_faxNumber", number: pharmacyInfo.Fax, countryId: defaultCountry.Id }) }]
                : [],
            model: null
        });
    }
}
